import type { ReactNode } from 'react';

interface BaseProps {
  id: string;
  label: string;
  required?: boolean | undefined;
  error?: string | undefined;
  hint?: string | undefined;
  children: ReactNode;
}

/**
 * Label + control + error wrapper. Wires the control to its label (htmlFor/id)
 * and its error to `aria-describedby` (set by the caller on the control).
 */
export function FormField({ id, label, required, error, hint, children }: BaseProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-semibold text-brand-navy">
        {label}
        {required && <span className="ml-0.5 text-red-600" aria-hidden="true">*</span>}
      </label>
      {children}
      {hint && !error && (
        <p id={`${id}-hint`} className="text-xs text-gray-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} role="alert" className="text-xs font-medium text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

const baseControl =
  'w-full rounded-lg border bg-white px-3 py-2.5 text-gray-900 outline-none transition placeholder-gray-400 focus-visible:ring-2 focus-visible:ring-brand-blue';

export function controlClass(hasError?: boolean): string {
  return `${baseControl} ${hasError ? 'border-red-400' : 'border-gray-300'}`;
}
